import classNames from 'classnames'
import { useContext } from 'react'
import { generatePath, Link, NavLink, useMatch } from 'react-router-dom'
import { AppRoute } from '../../types/AppRoute'
import routes from '../Router/routes'
import { userContext } from '../../context/UserContext'
import { FOCUS_VISIBLE_RING_OFFSET, TEXT_LINK_FOCUS_RING } from '../../lib/a11yFocus'
import { isSampleMenuPath, isSampleState } from '../../lib/sampleState'

type MenuItemProps = Pick<AppRoute, 'name'|'path'|'icon'|'menuName'|'id'> & {
	childPages: AppRoute[],
};

// Fixed-width first column for the icon (or an empty spacer on child rows).
// A real grid track's size is never affected by its content, unlike a flex
// item's implicit min-width:auto floor - so this guarantees the label column
// starts at the same x regardless of how wide any Font Awesome glyph renders.
export const NAV_ROW_GRID_CLASS = 'grid grid-cols-[1rem_1fr] items-center gap-x-md'

// Shared by the full sidebar and the picker sidebar so the Limited-account notice can't drift between layouts.
export const LoggedInAs = ({ fullName, canRequestUpgrade }: { fullName?: string; canRequestUpgrade?: boolean }) => (
	<>
		<div className='text-xs'>Logged in as {fullName}</div>
		{canRequestUpgrade && (
			<div className='text-xs text-charcoal-300'>
				(Limited account,{' '}
				<Link to={routes.companySettings.path} className={`text-link ${TEXT_LINK_FOCUS_RING}`}>upgrade now</Link>)
			</div>
		)}
	</>
)

export const MenuItem = ({ name, path, icon, menuName, childPages }: MenuItemProps) => {
	let expanded: boolean = false

	// If the route is parent path matched and there are children, we should show the parent as expanded
	const inParentPath = useMatch(path + '/*')
	if (inParentPath !== null) {
		expanded = true
	}

	return (
		<>
			<NavLink
				end
				to={ generatePath(path) }
				className={({ isActive }) =>
					`${
						isActive ? 'text-charcoal-900 bg-algae-200' : 'text-charcoal-050 hover:text-white'
						} ${NAV_ROW_GRID_CLASS} p-md text-sm rounded mb-md ${FOCUS_VISIBLE_RING_OFFSET}`
				}
			>
				{({ isActive }) => (
					<>
						<span
							className={`text-center ${icon} ${isActive ? 'text-charcoal-500' : 'text-algae-200'}`}
						></span>
						<div className='flex justify-between items-center w-full'>
							<span>{menuName ? menuName : name}</span>

							{/* Show a chevron if there's children */}
							{childPages.length > 0 && (
								<>
									<span
										className={classNames('fa-solid pr-1', {
											'fa-chevron-down': expanded,
											'fa-chevron-right': !expanded,
											'text-charcoal-500': isActive,
											'text-white': !isActive,
										})}
									></span>
								</>
							)}
						</div>
					</>
				)}
			</NavLink>

			{/* Render any children pages for this ID */}
			{expanded &&
				childPages.map((menuItem) => (
					<NavLink
						key={menuItem.path}
						to={ generatePath(menuItem.path) }
						className={({ isActive }) =>
							`${
								isActive ? 'text-charcoal-900 bg-algae-200' : 'text-algae-500 hover:text-white'
							} ${NAV_ROW_GRID_CLASS} p-md text-sm rounded mb-md ${FOCUS_VISIBLE_RING_OFFSET}`
						}
					>
						<span aria-hidden='true'></span>
						<span>{menuItem.menuName ? menuItem.menuName : menuItem.name}</span>
					</NavLink>
				))}
		</>
	)
}

type SidebarProps = {
	validRoutes: AppRoute[];
}

const Sidebar = ({validRoutes: allRoutes}: SidebarProps) => {
	// const userData = useUserData();
	const { userData } = useContext(userContext);

	if(!userData) {
		return null;
	}

	const { companyName, fullName, access, canRequestUpgrade } = userData;

	const validRoutes = isSampleState(userData)
		? allRoutes.filter(route => route.showAtBottom || isSampleMenuPath(route.path))
		: allRoutes;

	// const permittedroutes = orderedRoutes.filter(({rolesAllowed, accessRequired, parentId, showInSidebar}) => {
	// 	const hasRole = !rolesAllowed || rolesAllowed.includes(role) && typeof parentId === 'undefined';
	// 	const hasPermission = !accessRequired || accessRequired.every(accessString => accessStrings[accessString])
	// 	return hasRole && hasPermission && showInSidebar;
	// })

	const shownRoutes = validRoutes.filter(route => route.showInSidebar && !route.parentId);

	const topRoutes = shownRoutes.filter((route) => !route.showAtBottom && route.showInSidebar)
	const bottomRoutes = shownRoutes.filter((route) => route.showAtBottom && route.showInSidebar)

	return (
		<>
			<div
				data-testid='sidebar'
				className='w-1/6 md:w-1/5 p-sm bg-charcoal-700 flex flex-col shrink-0'
			>
				<h1 className='text-charcoal-050 mt-1 ml-4 mb-2'>
					<LoggedInAs fullName={fullName} canRequestUpgrade={canRequestUpgrade} />
				</h1>
				{access?.assumedCompanyType && (
					<div
						className='bg-sun-500 mt-1 mx-2 mb-2 p-md rounded'
						
					>
						<div className="text-xs flex">
							<div className="grow">Accessing</div>
							<Link to={routes.clearAssumedCompany.path}>
								<span className="fa-solid fa-xmark" />
							</Link>
						</div>
						<div>{companyName}</div>
						{access.user.permissions.write === false && <div className='text-xs'>View only</div>}
					</div>
				)}
				<nav className='p-md h-full flex flex-col justify-between pb-[calc(theme(spacing.md)+env(safe-area-inset-bottom))]' aria-label='Sidebar'>
					<div>
						{topRoutes.map(
							({ path, name, icon, menuName, id }: AppRoute) => {
								// Show as top level menu item if:
								//	- Enabled in side bar
								// 	- Is not a child page (has a parentId)
								const childPages = validRoutes.filter(route => {
									return id && route.parentId === id && route.showInSidebar
								});
								return (
									<MenuItem
										key={path}
										name={name}
										icon={icon}
										path={path}
										menuName={menuName}
										childPages={childPages}
									/>
								)
							}
						)}
					</div>

					{/* Bottom aligned menu items */}
					<div>
						{bottomRoutes.map(
							({ path, name, icon, menuName, id}: AppRoute) => {
								const childPages = topRoutes.filter(route => {
									return id && route.parentId === id
								});
								return (
									<MenuItem
										key={path}
										name={name}
										icon={icon}
										path={path}
										menuName={menuName}
										childPages={childPages}
									/>
								)
							}
						)}
						<Link
						className={`text-charcoal-050 hover:text-white ${NAV_ROW_GRID_CLASS} p-md text-sm rounded ${FOCUS_VISIBLE_RING_OFFSET}`}
							to={routes.publicLogout.path}
						>
							<span className='text-center fa-solid fa-right-from-bracket'></span>
							<span>Logout</span>
						</Link>
					</div>
				</nav>
			</div>
		</>
	)
}

export default Sidebar
